/**
 * SelectionManager
 * A generic utility class for handling selection of items in various UI components.
 * Supports single selection, multi-selection (with Ctrl/Command), range selection (with Shift),
 * and drag selection.
 */

import { IS_MOBILE } from '@constants/global';

export class SelectionManager {
  constructor(options) {
    this.container = options.container;
    this.itemSelector = options.itemSelector || '*';
    this.selectedClass = options.selectedClass || 'selected';
    this.addedClass = options.addedClass || '';
    this.getItemId = options.getItemId || (el => el.id);
    this.onSelectionChange = options.onSelectionChange || (() => {});
    this.canSelectItem = options.canSelectItem || (() => true);
    this.canSelectParent = options.canSelectParent || (() => false);
    this.onDeleteKey = options.onDeleteKey || null;

    // Data management
    this.itemsMap = new Map(); // Store all items by their IDs
    this.selectedItems = new Set(); // Store selected items data
    this.getItemData = options.getItemData || (el => el); // Function to extract item data from element

    // Copy-paste handling
    this.onCopy = options.onCopy || null;
    this.onPaste = options.onPaste || null;
    this.copiedItemIds = [];
    this.copyInProgressClass =
      options.copyInProgressClass || 'copy-in-progress';

    // Selection options
    this.enableShiftSelect = options.enableShiftSelect !== false;
    this.enableCtrlSelect = options.enableCtrlSelect !== false;
    this.enableDragSelect = options.enableDragSelect !== false;
    this.enableRightClickSelect = options.enableRightClickSelect !== false;
    this.clearSelectionOnOutsideClick =
      options.clearSelectionOnOutsideClick !== false;
    this.ignoreSelector = options.ignoreSelector || '';

    // Selection state
    this.selectedIds = new Set();
    this.lastSelectedElement = null;
    this.lastCtrlSelectedElement = null; // Tracking the most recent Ctrl-selected item

    // Drag selection state
    this.isDragging = false;
    this.dragStartX = 0;
    this.dragStartY = 0;
    this.isShiftDrag = false;
    this.isCtrlDrag = false;
    this.didDrag = false;
    this.initialMouseDownTarget = null;

    // Create drag selection box
    if (this.enableDragSelect) {
      this.dragSelectionBox = document.createElement('div');
      this.dragSelectionBox.className = 'drag-selection-box';
      this.dragSelectionBox.style.display = 'none';
      this.dragSelectionBox.style.position = 'fixed';
      this.dragSelectionBox.style.border = '1px solid #0078d4';
      this.dragSelectionBox.style.backgroundColor = 'rgba(0, 120, 212, 0.1)';
      this.dragSelectionBox.style.pointerEvents = 'none';
      this.dragSelectionBox.style.zIndex = '9999';
      this.container.appendChild(this.dragSelectionBox);
    }

    this.boundHandleKeyDown = this.handleKeyDown.bind(this);
    this.boundHandleDocumentClick = this.handleDocumentClick.bind(this);

    this.initialize();
  }

  /**
   * Initialize event handlers
   */
  initialize() {
    // Click handler for item selection
    this.container.addEventListener(
      'click',
      this.handleContainerClick.bind(this),
    );

    // Optional right-click selection
    if (this.enableRightClickSelect) {
      this.container.addEventListener(
        'contextmenu',
        this.handleContextMenu.bind(this),
      );
    }

    // Escape key to clear selection
    document.addEventListener('keydown', this.boundHandleKeyDown);

    // Clear selection on outside click
    if (this.clearSelectionOnOutsideClick) {
      document.addEventListener('click', this.boundHandleDocumentClick);
    }

    // Setup drag selection
    if (this.enableDragSelect) {
      this.setupDragSelection();
    }
  }

  /**
   * Handle container click events (delegation pattern)
   */
  handleContainerClick(e) {
    // Skip if clicking on ignored elements
    if (this.ignoreSelector && e.target.closest(this.ignoreSelector)) {
      return;
    }

    // Find the clicked item
    const item = e.target.closest(this.itemSelector);
    if (!item) return;

    // Check if the item can be selected
    if (!this.canSelectItem(item)) return;

    // Check if parent is selected (optional hierarchical selection)
    if (this.canSelectParent(item)) return;

    // Mobile multi-select mode: behave like desktop Ctrl+click (additive selection)
    if (IS_MOBILE()) {
      this.toggleItemSelection(item);
      this.lastCtrlSelectedElement = item; // Track for potential shift selection
      return;
    }

    // Handle ctrl+shift+click (range selection from last Ctrl-selected item)
    if (
      this.enableShiftSelect &&
      this.enableCtrlSelect &&
      e.shiftKey &&
      (e.ctrlKey || e.metaKey) &&
      this.lastCtrlSelectedElement
    ) {
      this.handleCtrlShiftSelect(item);
    }
    // Handle shift+click range selection from last selected item
    else if (this.enableShiftSelect && e.shiftKey && this.lastSelectedElement) {
      this.handleShiftSelect(item);
    }
    // Handle ctrl/cmd+click multi-selection
    else if (this.enableCtrlSelect && (e.ctrlKey || e.metaKey)) {
      this.toggleItemSelection(item);
      this.lastCtrlSelectedElement = item; // Track the last Ctrl-selected item
    }
    // Regular click (replace selection)
    else {
      this.selectSingleItem(item);
      this.lastCtrlSelectedElement = null; // Reset Ctrl selection tracking on regular click
    }
  }

  /**
   * Handle right-click selection
   */
  handleContextMenu(e) {
    if (this.ignoreSelector && e.target.closest(this.ignoreSelector)) {
      return;
    }

    const item = e.target.closest(this.itemSelector);
    if (!item || !this.canSelectItem(item)) return;

    // If item is not already selected, select only this item
    if (!this.isItemSelected(item)) {
      this.selectSingleItem(item);
    }

    // Don't prevent default here - let the caller handle the context menu
  }

  /**
   * Handle keyboard events (e.g., Escape to clear selection)
   */
  handleKeyDown(e) {
    const isActiveElementInContainer =
      this.container.contains(document.activeElement) ||
      document.activeElement === document.body;

    if (!isActiveElementInContainer) {
      return;
    }

    if (e.key === 'Escape') {
      this.clearCopiedItems();
      this.clearSelection();
    }

    if (
      (e.key === 'Delete' || e.key === 'Del') &&
      this.onDeleteKey &&
      this.selectedIds.size > 0
    ) {
      e.preventDefault();

      const selectedItems = this.getSelectedItems();
      this.onDeleteKey(selectedItems, this.selectedIds);
    }

    // Handle copy (Ctrl+C / Cmd+C)
    if (
      (e.ctrlKey || e.metaKey) &&
      e.key === 'c' &&
      this.onCopy &&
      this.selectedIds.size > 0
    ) {
      e.preventDefault();
      this.copiedItemIds = Array.from(this.selectedIds);
      this.markCopiedItems();
      if (this.onCopy) {
        this.onCopy(this.copiedItemIds);
      }
    }

    // Handle paste (Ctrl+V / Cmd+V)
    if (
      (e.ctrlKey || e.metaKey) &&
      e.key === 'v' &&
      this.onPaste &&
      this.copiedItemIds.length > 0
    ) {
      e.preventDefault();
      if (this.onPaste) {
        this.onPaste(this.copiedItemIds);
      }
    }
  }

  markCopiedItems() {
    const allItems = this.container.querySelectorAll(this.itemSelector);
    Array.from(allItems).forEach(item => {
      if (item.setCopyInProgress) {
        item.setCopyInProgress(false);
      } else if (item.classList.contains(this.copyInProgressClass)) {
        item.classList.remove(this.copyInProgressClass);
      }
    });

    this.copiedItemIds.forEach(id => {
      const matchingItems = Array.from(allItems).filter(
        item => this.getItemId(item) === id,
      );

      matchingItems.forEach(item => {
        if (item) {
          if (item.setCopyInProgress) {
            item.setCopyInProgress(true);
          } else {
            item.classList.add(this.copyInProgressClass);
          }
        }
      });
    });
  }

  clearCopiedItems(removeHighlightOnly = false) {
    if (!removeHighlightOnly) {
      this.copiedItemIds = [];
    }

    const allItems = this.container.querySelectorAll(this.itemSelector);
    Array.from(allItems).forEach(item => {
      if (removeHighlightOnly) {
        if (item.setCopyInProgress) {
          item.setCopyInProgress(false);
        } else if (item.classList.contains(this.copyInProgressClass)) {
          item.classList.remove(this.copyInProgressClass);
        }
      } else {
        if (item.setCopyInProgress) {
          item.setCopyInProgress(false);
        }
        item.classList.remove(this.copyInProgressClass);
      }
    });
  }

  /**
   * Handle clicks outside the container
   */
  handleDocumentClick(e) {
    // On mobile, don't clear selection if clicking on a selectable item
    if (IS_MOBILE() && e.target.closest(this.itemSelector)) return;

    if (
      !this.container.contains(e.target) &&
      !e.target.closest('.context-menu') &&
      !e.target.closest('dialog') &&
      !e.target.closest(this.ignoreSelector)
    ) {
      this.clearSelection();
    }
  }

  /**
   * Set up drag selection
   */
  setupDragSelection() {
    const preventTextSelection = e => {
      if (this.isDragging) {
        e.preventDefault();
      }
    };

    this.container.addEventListener('selectstart', preventTextSelection);

    this.container.addEventListener('mousedown', e => {
      // Skip drag if clicking on ignored elements
      if (this.ignoreSelector && e.target.closest(this.ignoreSelector)) {
        return;
      }

      e.preventDefault();
      this.isDragging = true;
      this.dragStartX = e.clientX;
      this.dragStartY = e.clientY;
      this.isShiftDrag = e.shiftKey;
      this.isCtrlDrag = e.ctrlKey || e.metaKey;

      // Only clear selection for drag operation if NOT on mobile
      if (!this.isShiftDrag && !this.isCtrlDrag && !IS_MOBILE()) {
        this.clearSelection();
      }

      const handleMouseMove = e => {
        if (!this.isDragging) return;

        const x = Math.min(e.clientX, this.dragStartX);
        const y = Math.min(e.clientY, this.dragStartY);
        const width = Math.abs(e.clientX - this.dragStartX);
        const height = Math.abs(e.clientY - this.dragStartY);

        // Update the selection box visuals
        this.dragSelectionBox.style.left = `${x}px`;
        this.dragSelectionBox.style.top = `${y}px`;
        this.dragSelectionBox.style.width = `${width}px`;
        this.dragSelectionBox.style.height = `${height}px`;
        this.dragSelectionBox.style.display = 'block';

        // Handle the selection based on what's in the drag box
        this.handleDragSelection(x, y, width, height);
      };

      const handleMouseUp = () => {
        if (!this.isDragging) return;
        this.isDragging = false;
        this.dragSelectionBox.style.display = 'none';

        // Trigger selection change event
        this.triggerSelectionChange();

        // Remove the temporary mouse move and up handlers
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };

      // Attach temporary handlers for this drag operation
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    });
  }

  /**
   * Handle drag selection by checking which items are within the selection box
   */
  handleDragSelection(x, y, width, height) {
    const selectionRect = {
      left: x,
      top: y,
      right: x + width,
      bottom: y + height,
    };

    // Get all selectable items
    const selectableItems = Array.from(
      this.container.querySelectorAll(this.itemSelector),
    );

    selectableItems.forEach(item => {
      // Skip items that can't be selected
      if (!this.canSelectItem(item) || this.canSelectParent(item)) return;

      const itemRect = item.getBoundingClientRect();
      const isIntersecting = !(
        itemRect.right < selectionRect.left ||
        itemRect.left > selectionRect.right ||
        itemRect.bottom < selectionRect.top ||
        itemRect.top > selectionRect.bottom
      );

      if (isIntersecting) {
        // Add to selection
        this.addItemToSelection(item);
      } else if (!this.isShiftDrag && !this.isCtrlDrag) {
        // Remove from selection only if not doing a multi-select operation
        this.removeItemFromSelection(item);
      }
    });
  }

  /**
   * Handle Ctrl+Shift+click selection to select a range from the last Ctrl-selected item
   * This matches Windows explorer and spreadsheet behavior
   */
  handleCtrlShiftSelect(currentItem) {
    if (!this.lastCtrlSelectedElement) return;

    // Get all selectable items
    const selectableItems = Array.from(
      this.container.querySelectorAll(this.itemSelector),
    ).filter(item => this.canSelectItem(item) && !this.canSelectParent(item));

    // Find indices of current and last Ctrl-selected items
    const currentIndex = selectableItems.indexOf(currentItem);
    const lastCtrlIndex = selectableItems.indexOf(this.lastCtrlSelectedElement);

    if (currentIndex === -1 || lastCtrlIndex === -1) return;

    // Select the range without clearing existing selection (add to it)
    const startIndex = Math.min(currentIndex, lastCtrlIndex);
    const endIndex = Math.max(currentIndex, lastCtrlIndex);

    for (let i = startIndex; i <= endIndex; i++) {
      this.addItemToSelection(selectableItems[i]);
    }

    this.lastSelectedElement = currentItem;
    this.triggerSelectionChange();
  }

  /**
   * Handle shift-click range selection
   */
  handleShiftSelect(currentItem) {
    if (!this.lastSelectedElement) return;

    // Get all selectable items
    const selectableItems = Array.from(
      this.container.querySelectorAll(this.itemSelector),
    ).filter(item => this.canSelectItem(item) && !this.canSelectParent(item));

    // Find indices of current and last selected items
    const currentIndex = selectableItems.indexOf(currentItem);
    const lastIndex = selectableItems.indexOf(this.lastSelectedElement);

    if (currentIndex === -1 || lastIndex === -1) return;

    // Clear current selection
    if (!this.isCtrlDrag) {
      this.clearSelection();
    }

    // Select the range
    const startIndex = Math.min(currentIndex, lastIndex);
    const endIndex = Math.max(currentIndex, lastIndex);

    for (let i = startIndex; i <= endIndex; i++) {
      this.addItemToSelection(selectableItems[i]);
    }

    this.lastSelectedElement = currentItem;
    this.triggerSelectionChange();
  }

  /**
   * Toggle selection state of an item
   */
  toggleItemSelection(item) {
    const id = this.getItemId(item);

    if (this.selectedIds.has(id)) {
      this.removeItemFromSelection(item);
    } else {
      this.addItemToSelection(item);
    }

    this.lastSelectedElement = item;
    this.triggerSelectionChange();
  }

  /**
   * Select a single item (clearing previous selection)
   */
  selectSingleItem(item) {
    this.clearSelection();
    this.addItemToSelection(item);
    this.lastSelectedElement = item;
    this.triggerSelectionChange();
  }

  /**
   * Register an item in the items map
   */
  registerItem(element, data) {
    const id = this.getItemId(element);
    this.itemsMap.set(id, data);
  }

  /**
   * Get all registered items
   */
  getAllItems() {
    return this.itemsMap;
  }

  /**
   * Get all selected items data
   */
  getSelectedItemsData() {
    return this.selectedItems;
  }

  /**
   * Add an item to the current selection
   */
  addItemToSelection(item) {
    const id = this.getItemId(item);
    const itemData = this.itemsMap.get(id);
    if (itemData) {
      this.selectedIds.add(id);
      this.selectedItems.add(itemData);
      item.classList.add(this.selectedClass);
      if (this.addedClass) {
        item.classList.add(...this.addedClass.split(' '));
      }
    }
  }

  /**
   * Remove an item from the current selection
   */
  removeItemFromSelection(item) {
    const id = this.getItemId(item);
    const itemData = this.itemsMap.get(id);
    if (itemData) {
      this.selectedIds.delete(id);
      this.selectedItems.delete(itemData);
      item.classList.remove(this.selectedClass);
      if (this.addedClass) {
        item.classList.remove(...this.addedClass.split(' '));
      }
    }
  }

  /**
   * Check if an item is currently selected
   */
  isItemSelected(item) {
    const id = this.getItemId(item);
    return this.selectedIds.has(id);
  }

  /**
   * Clear the entire selection
   */
  clearSelection() {
    this.container.querySelectorAll(`.${this.selectedClass}`).forEach(item => {
      item.classList.remove(this.selectedClass);
      if (this.addedClass) {
        item.classList.remove(...this.addedClass.split(' '));
      }
    });

    this.selectedIds.clear();
    this.selectedItems.clear();
    this.lastSelectedElement = null;
    this.lastCtrlSelectedElement = null;
    this.triggerSelectionChange();
    this.clearCopiedItems(true);
  }

  /**
   * Get all currently selected items
   */
  getSelectedItems() {
    return Array.from(
      this.container.querySelectorAll(`.${this.selectedClass}`),
    );
  }

  /**
   * Get all currently selected item IDs
   */
  getSelectedIds() {
    return Array.from(this.selectedIds);
  }

  /**
   * Manually select specific items by their IDs
   */
  selectItemsByIds(ids) {
    this.clearSelection();

    ids.forEach(id => {
      const selector = `${this.itemSelector}[data-id="${id}"]`;
      const item = this.container.querySelector(selector);
      if (item && this.canSelectItem(item)) {
        this.addItemToSelection(item);
        this.lastSelectedElement = item;
      }
    });

    this.triggerSelectionChange();
  }

  /**
   * Trigger the selection change callback
   */
  triggerSelectionChange() {
    const selectedElements = this.getSelectedItems();
    this.onSelectionChange(selectedElements, this.selectedItems);
  }

  /**
   * Clean up event listeners when no longer needed
   */
  destroy() {
    document.removeEventListener('keydown', this.boundHandleKeyDown);
    if (this.clearSelectionOnOutsideClick) {
      document.removeEventListener('click', this.boundHandleDocumentClick);
    }
    if (this.enableDragSelect && this.dragSelectionBox.parentNode) {
      this.dragSelectionBox.parentNode.removeChild(this.dragSelectionBox);
    }
  }
}
